import { useEffect, useState, useCallback, useMemo } from "react";
import { useToast } from "../../hooks/useToast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Modal from "../../components/common/Modal";
import ListLoadError from "../../components/admin/ListLoadError.jsx";
import api from "../../services/api";
import styles from "./ActivityLogsPage.module.css";

const ACTION_LABELS = {
  all: "All Actions",
  drcare_submitted: "DrCare Submitted",
  drcare_approved: "DrCare Approved",
  drcare_denied: "DrCare Denied",
  payment_processed: "Payment Processed",
  api_error: "API Error",
  user_login: "User Login",
  user_registered: "User Registered",
  webhook_received: "Webhook Received",
  csv_import: "CSV Import",
  admin_action: "Admin Action",
  created: "Created",
  updated: "Updated",
  deleted: "Deleted"
};

// `logs.type` is a small open set, and the API now reports it in full (card 28570). The Type
// filter is derived from that report rather than from a literal, so a value the audit trail
// actually contains -- `action` (the largest bucket), `migration`, `security` -- can never again
// be unreachable from the only page that surfaces the audit trail. These three are the floor the
// API guarantees; they are used on their own only if /stats itself fails to load.
const FALLBACK_TYPES = ["error", "info", "warning"];

const TYPE_LABELS = {
  error: "Errors",
  info: "Info",
  warning: "Warnings"
};

const formatTypeLabel = (type) =>
  TYPE_LABELS[type] ?? type.charAt(0).toUpperCase() + type.slice(1);

const ACTION_COLORS = {
  drcare_submitted: "#3b82f6",
  drcare_approved: "#10b981",
  drcare_denied: "#ef4444",
  payment_processed: "#8b5cf6",
  api_error: "#ef4444",
  user_login: "#6b7280",
  user_registered: "#10b981",
  webhook_received: "#f59e0b",
  csv_import: "#06b6d4",
  admin_action: "#f97316",
  created: "#10b981",
  updated: "#3b82f6",
  deleted: "#ef4444"
};

const TYPE_COLORS = {
  error: "#ef4444",
  info: "#3b82f6",
  warning: "#f59e0b"
};

const formatDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true
  });
};

const ActivityLogsPage = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statsError, setStatsError] = useState(false);

  // Filters
  const [actionFilter, setActionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState("");

  // Offer every type the current window actually contains, plus the floor the API guarantees,
  // plus whatever is selected right now -- so narrowing the date range can never blank the control.
  const typeOptions = useMemo(() => {
    const present = Object.keys(stats?.typeCounts ?? {});
    const union = new Set([...FALLBACK_TYPES, ...present]);
    if (typeFilter !== "all") union.add(typeFilter);
    return [...union].sort();
  }, [stats, typeFilter]);

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (actionFilter !== "all") params.set("action", actionFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);
      if (search) params.set("search", search);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);

      const res = await api.get(`/admin/activity-logs?${params}`);
      setLogs(res.logs || []);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message || "Failed to load logs");
      toast.error(err.message || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [actionFilter, typeFilter, search, dateFrom, dateTo]);

  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      const res = await api.get(`/admin/activity-logs/stats?${params}`);
      setStats(res);
      setStatsError(false);
    } catch {
      // Non-fatal, but the tiles must NOT keep showing the previous
      // range's numbers as if they were measured for this one.
      setStatsError(true);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [actionFilter, typeFilter, dateFrom, dateTo]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadLogs();
  };

  const handleViewDetail = async (log) => {
    try {
      const res = await api.get(`/admin/activity-logs/${log.id}`);
      setSelectedLog(res.log);
      setDetailModalOpen(true);
      setResolveNote("");
    } catch (err) {
      toast.error("Failed to load log detail");
    }
  };

  const handleResolve = async () => {
    if (!selectedLog) return;
    try {
      await api.patch(`/admin/activity-logs/${selectedLog.id}/resolve`, {
        note: resolveNote
      });
      toast.success("Marked as resolved");
      setSelectedLog(prev => ({ ...prev, is_resolved: true }));
      loadLogs(pagination?.page || 1);
      loadStats();
    } catch (err) {
      toast.error(err.message || "Failed to resolve");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Activity Logs</h1>
          <p className={styles.subtitle}>System events, API calls, errors, and audit trail</p>
        </div>
        <button className={styles.refreshBtn} onClick={() => { loadLogs(); loadStats(); }}>
          Refresh
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{statsError ? "—" : stats.total}</div>
            <div className={styles.statLabel}>Events (7d)</div>
          </div>
          <div className={`${styles.statCard} ${styles.statError}`}>
            <div className={styles.statNumber}>{statsError ? "—" : stats.typeCounts?.error || 0}</div>
            <div className={styles.statLabel}>Errors</div>
          </div>
          <div className={`${styles.statCard} ${styles.statSuccess}`}>
            <div className={styles.statNumber}>{statsError ? "—" : stats.actionCounts?.drcare_approved || 0}</div>
            <div className={styles.statLabel}>DrCare Approved</div>
          </div>
          <div className={`${styles.statCard} ${styles.statWarning}`}>
            <div className={styles.statNumber}>{statsError ? "—" : stats.actionCounts?.drcare_denied || 0}</div>
            <div className={styles.statLabel}>DrCare Denied</div>
          </div>
          <div className={`${styles.statCard} ${styles.statInfo}`}>
            <div className={styles.statNumber}>{statsError ? "—" : stats.actionCounts?.payment_processed || 0}</div>
            <div className={styles.statLabel}>Payments</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className={styles.filterSelect}
        >
          {Object.entries(ACTION_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="all">All Types</option>
          {typeOptions.map((type) => (
            <option key={type} value={type}>{formatTypeLabel(type)}</option>
          ))}
        </select>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={styles.filterDate}
          placeholder="From"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={styles.filterDate}
          placeholder="To"
        />

        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages..."
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchBtn}>Search</button>
        </form>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Type</th>
                  <th>Message</th>
                  <th>User</th>
                  <th>Entity</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr><td colSpan={8} className={styles.empty}>
                    <ListLoadError
                      message={error}
                      onRetry={() => { loadLogs(pagination?.page || 1); loadStats(); }}
                    />
                  </td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={8} className={styles.empty}>No logs found</td></tr>
                ) : logs.map((log) => (
                  <tr key={log.id} className={log.type === 'error' ? styles.errorRow : ''}>
                    <td className={styles.timeCell}>{formatDate(log.created_at)}</td>
                    <td>
                      <span
                        className={styles.actionBadge}
                        style={{ backgroundColor: (ACTION_COLORS[log.action] || '#6b7280') + '18', color: ACTION_COLORS[log.action] || '#6b7280', borderColor: (ACTION_COLORS[log.action] || '#6b7280') + '30' }}
                      >
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td>
                      {log.type && (
                        <span
                          className={styles.typeBadge}
                          style={{ color: TYPE_COLORS[log.type] || '#6b7280' }}
                        >
                          {log.type}
                        </span>
                      )}
                    </td>
                    <td className={styles.messageCell}>
                      {log.message ? (log.message.length > 80 ? log.message.slice(0, 80) + '...' : log.message) : '-'}
                    </td>
                    <td className={styles.userCell}>{log.user_email || '-'}</td>
                    <td className={styles.entityCell}>
                      {log.entity_type ? `${log.entity_type}` : '-'}
                    </td>
                    <td>
                      {log.type === 'error' && (
                        log.is_resolved
                          ? <span className={styles.resolved}>Resolved</span>
                          : <span className={styles.unresolved}>Open</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={styles.viewBtn}
                        onClick={() => handleViewDetail(log)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadLogs(pagination.page - 1)}
                className={styles.pageBtn}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => loadLogs(pagination.page + 1)}
                className={styles.pageBtn}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      
        <Modal isOpen={detailModalOpen && !!selectedLog} onClose={() => setDetailModalOpen(false)} title="Log Detail" size="large">
          {selectedLog && <div className={styles.detailModal}>
            <div className={styles.detailGrid}>
              <div className={styles.detailField}>
                <label>Time</label>
                <span>{formatDate(selectedLog.created_at)}</span>
              </div>
              <div className={styles.detailField}>
                <label>Action</label>
                <span
                  className={styles.actionBadge}
                  style={{ backgroundColor: (ACTION_COLORS[selectedLog.action] || '#6b7280') + '18', color: ACTION_COLORS[selectedLog.action] || '#6b7280' }}
                >
                  {ACTION_LABELS[selectedLog.action] || selectedLog.action}
                </span>
              </div>
              <div className={styles.detailField}>
                <label>Type</label>
                <span style={{ color: TYPE_COLORS[selectedLog.type] || '#6b7280' }}>{selectedLog.type || '-'}</span>
              </div>
              <div className={styles.detailField}>
                <label>User</label>
                <span>{selectedLog.user_email || '-'}</span>
              </div>
              <div className={styles.detailField}>
                <label>Entity</label>
                <span>{selectedLog.entity_type || '-'}{selectedLog.entity_id ? ` (${selectedLog.entity_id.slice(0, 8)}...)` : ''}</span>
              </div>
              <div className={styles.detailField}>
                <label>Environment</label>
                <span>{selectedLog.environment || '-'}</span>
              </div>
            </div>

            <div className={styles.detailField}>
              <label>Message</label>
              <p className={styles.detailMessage}>{selectedLog.message || '-'}</p>
            </div>

            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <div className={styles.detailField}>
                <label>Details</label>
                <pre className={styles.jsonBlock}>{JSON.stringify(selectedLog.details, null, 2)}</pre>
              </div>
            )}

            {selectedLog.error && Object.keys(selectedLog.error).length > 0 && (
              <div className={styles.detailField}>
                <label>Error</label>
                <pre className={`${styles.jsonBlock} ${styles.errorJson}`}>{JSON.stringify(selectedLog.error, null, 2)}</pre>
              </div>
            )}

            {selectedLog.request && Object.keys(selectedLog.request).length > 0 && (
              <div className={styles.detailField}>
                <label>Request</label>
                <pre className={styles.jsonBlock}>{JSON.stringify(selectedLog.request, null, 2)}</pre>
              </div>
            )}

            {selectedLog.response && Object.keys(selectedLog.response).length > 0 && (
              <div className={styles.detailField}>
                <label>Response</label>
                <pre className={styles.jsonBlock}>{JSON.stringify(selectedLog.response, null, 2)}</pre>
              </div>
            )}

            {selectedLog.type === 'error' && !selectedLog.is_resolved && (
              <div className={styles.resolveSection}>
                <h3>Mark as Resolved</h3>
                <textarea
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  placeholder="Resolution note (optional)..."
                  className={styles.resolveInput}
                />
                <button className={styles.resolveBtn} onClick={handleResolve}>
                  Mark Resolved
                </button>
              </div>
            )}

            {selectedLog.is_resolved && (
              <div className={styles.resolvedBanner}>
                Resolved {selectedLog.resolved_at ? formatDate(selectedLog.resolved_at) : ''}
                {selectedLog.resolution_note && ` — ${selectedLog.resolution_note}`}
              </div>
            )}
          </div>}
        </Modal>
    </div>
  );
};

export default ActivityLogsPage;
